/**
 * Reusable layout helpers for the win/replay overlay UI: icon buttons,
 * the replay-controls button row, the playback timeline track, and the
 * hamburger restore button. Extracted from `GameScene` so the layout math
 * lives next to the visuals it produces.
 *
 * These functions are deliberately pure — they create Pixi nodes against a
 * provided container and return the hit-rects + handles the host needs to
 * drive interactions. They never touch GameScene state directly, which
 * keeps GameScene's ownership of replay/overlay state intact while giving
 * us a place to grow this UI further (e.g. accessibility props, animated
 * progress markers).
 */

import { Container, Graphics } from 'pixi.js';
import { drawSmallIcon, type SmallIcon } from './Icons.js';

export interface OverlayHit {
  x: number; y: number; w: number; h: number;
  action: () => void;
}

/** Pixi node + hit rect for an overlay icon button. */
export interface IconButton {
  node: Container;
  hit: OverlayHit;
}

/** Build a 36×36-style icon button matching the editor toolbar look:
 *  dark fill, purple border, white icon glyph. */
export function makeIconButton(
  icon: SmallIcon,
  x: number, y: number, size: number,
  action: () => void,
): IconButton {
  const node = new Container();
  const bg = new Graphics();
  bg.roundRect(x, y, size, size, 6)
    .fill({ color: 0x1a1030, alpha: 0.94 })
    .stroke({ color: 0x3a2a55, width: 1 });
  node.addChild(bg);
  const ic = drawSmallIcon(icon, x + size / 2, y + size / 2);
  node.addChild(ic);
  return { node, hit: { x, y, w: size, h: size, action } };
}

/** Spec for a single button in the replay controls row. */
export interface ReplayButtonSpec {
  icon: SmallIcon;
  /** Tooltip text — currently unused but reserved for future a11y/hover. */
  title: string;
  action: () => void;
}

export interface ReplayButtonRowOptions {
  /** Container the buttons + timeline are appended to. */
  content: Container;
  /** Viewport width in CSS pixels (renderer.screen.width). */
  width: number;
  /** Viewport height in CSS pixels (renderer.screen.height). */
  height: number;
  buttons: ReplayButtonSpec[];
}

export interface ReplayButtonRowResult {
  hits: OverlayHit[];
  /** Graphics layer the host paints the playback progress fill into each
   *  frame. Already added to `content`. */
  timelineFill: Graphics;
  /** Bounds of the timeline track for the host's per-frame fill draw. */
  timelineRect: { x: number; y: number; w: number; h: number };
}

/** Lay out the row of icon buttons (Play / Replay / Share / Save / Copy / Load)
 *  centred under the win-overlay text, plus the playback timeline track
 *  underneath. The host owns the per-frame fill animation. */
export function buildReplayButtonRow(opts: ReplayButtonRowOptions): ReplayButtonRowResult {
  const { content, width: w, height: h, buttons } = opts;
  const SIZE = 36, GAP = 8;
  const totalW = SIZE * buttons.length + GAP * (buttons.length - 1);
  const startX = (w - totalW) / 2;
  const y = h / 2 + 80;

  const hits: OverlayHit[] = [];
  for (let i = 0; i < buttons.length; i++) {
    const it = buttons[i]!;
    const x = startX + i * (SIZE + GAP);
    const { node, hit } = makeIconButton(it.icon, x, y, SIZE, it.action);
    content.addChild(node);
    hits.push(hit);
  }

  // Timeline: thin horizontal bar showing playback progress, redrawn each
  // frame by the host into the returned `timelineFill`.
  const tlW = Math.min(420, w - 80);
  const tlH = 6;
  const tlX = (w - tlW) / 2;
  const tlY = y + SIZE + 18;
  const bg = new Graphics();
  bg.roundRect(tlX, tlY, tlW, tlH, tlH / 2)
    .fill({ color: 0xffffff, alpha: 0.18 });
  content.addChild(bg);
  const timelineFill = new Graphics();
  content.addChild(timelineFill);

  return { hits, timelineFill, timelineRect: { x: tlX, y: tlY, w: tlW, h: tlH } };
}

export interface HamburgerButtonOptions {
  /** Overlay container the button is drawn on (lives outside winContent so
   *  it stays visible after winContent fades). */
  overlay: Container;
  /** Viewport width in CSS pixels (renderer.screen.width). */
  width: number;
  action: () => void;
}

/** Floating hamburger anchored just below the HUD pause cluster. Returned
 *  with `visible=false, alpha=0` so the host can fade it in once the win
 *  content auto-hides. */
export function makeHamburgerButton(opts: HamburgerButtonOptions): IconButton {
  const SIZE = 36;
  const x = opts.width - SIZE - 12;
  const y = 12 + 36 + 8;
  const btn = makeIconButton('menu', x, y, SIZE, opts.action);
  btn.node.alpha = 0;
  btn.node.visible = false;
  opts.overlay.addChild(btn.node);
  return btn;
}
